// Panel de administración de usuarios del HCE (Rediseño mayor 2026-08-31,
// Módulo E) — solo para el rol "admin" (hce_users), lee su sesión propia de
// hceAdminSession.ts (NUNCA la de Medplum). Lista admin+Personal Médico del
// tenant, con el cupo usado/total, y permite dar de baja (libera cupo de
// inmediato) — dar de baja a un Personal Médico revoca también su acceso a
// Medplum, del lado del servidor (ver medplum-admin.ts).
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CATALOGO_ESPECIALIDADES_ORDENADO, especialidadPorCodigo, especialidadUsaPoliticaMedico } from '@/lib/catalogoEspecialidades';
import {
  actualizarConsultorioHceUsuario,
  actualizarEspecialidadHceUsuario,
  actualizarMatriculaHceUsuario,
  cambiarEstadoHceUsuario,
  enviarEnlaceContrasenaHceUsuario,
  invitarHceUsuario,
  listarHceUsuarios,
  reactivarHceUsuario,
  resumenBajaHceUsuario,
  type HceUsuarioListado,
  type ListaHceUsuarios,
} from '@/lib/hceVault';
import { cerrarSesionHceAdmin, leerSesionHceAdmin } from '@/auth/hceAdminSession';
import { avisoDeBaja, bajaNecesitaPanel, cargaDeBaja, type ResumenPreviaBaja } from '@/lib/bajaProfesional';
import { BajaMedicoPanel } from './BajaMedicoPanel';
import { ExportacionYRespaldo } from '@/dashboard/ExportacionYRespaldo';
import { desbloquearMfaDeCuenta, reiniciarMfaDeCuenta } from '@/lib/hceMfa';
import { SeguridadMfaPanel } from './SeguridadMfaPanel';

export function UsuariosPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sesion = leerSesionHceAdmin();
  const [datos, setDatos] = useState<ListaHceUsuarios | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendiente, setPendiente] = useState<number | null>(null);
  const [confirmando, setConfirmando] = useState<number | null>(null);
  // C11.3: cuadro previo a la baja de un médico (citas por atender y a quién pasarlas). `resumen: null` = leyéndolo del servidor.
  // Cuenta cuyo resumen se está leyendo: si la persona cancela mientras carga, la respuesta tardía no reabre el cuadro.
  const bajaCargando = useRef<number | null>(null);
  const [bajaPanel, setBajaPanel] = useState<{ id: number; resumen: ResumenPreviaBaja | null; error: string | null } | null>(null);
  const [sesionVencida, setSesionVencida] = useState(false);
  const [editandoMatricula, setEditandoMatricula] = useState<number | null>(null);
  const [matriculaEnEdicion, setMatriculaEnEdicion] = useState('');
  const [guardandoMatricula, setGuardandoMatricula] = useState(false);
  const [editandoEspecialidad, setEditandoEspecialidad] = useState<number | null>(null);
  const [especialidadEnEdicion, setEspecialidadEnEdicion] = useState('');
  const [guardandoEspecialidad, setGuardandoEspecialidad] = useState(false);
  // 1d: al pasar a una especialidad cuya política es la amplia de «Médico» se pide confirmar antes de guardar.
  const [confirmandoEspecialidad, setConfirmandoEspecialidad] = useState(false);
  // Sala del HCE (rebanada 5): consultorio habitual del médico, editable por el admin con el mismo patrón que la matrícula.
  const [editandoConsultorio, setEditandoConsultorio] = useState<number | null>(null);
  const [consultorioEnEdicion, setConsultorioEnEdicion] = useState('');
  const [guardandoConsultorio, setGuardandoConsultorio] = useState(false);
  // Admin mínimo (2026-09-13, retiro del legado packages/hce): invitar,
  // cambiar contraseña (por enlace al correo) y reactivar, desde esta pantalla.
  const [aviso, setAviso] = useState<string | null>(null);
  const [invitando, setInvitando] = useState(false);
  // MT-043-C: reinicio y desbloqueo de la verificación en dos pasos de OTRA cuenta; `version` fuerza releer la sesión cuando el panel la reemplaza.
  const [confirmandoMfa, setConfirmandoMfa] = useState<number | null>(null);
  const [, setVersion] = useState(0);

  const cargar = useCallback(async () => {
    if (!sesion) return;
    setCargando(true);
    try {
      const r = await listarHceUsuarios(sesion.token);
      if (r === null) {
        setSesionVencida(true);
        return;
      }
      setDatos(r);
    } catch {
      setError(t('admin.usuarios.errorCargar'));
    } finally {
      setCargando(false);
    }
  }, [sesion, t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** «Dar de baja»: un médico primero pasa por la lectura de lo que su baja deja sin médico; cualquier otra cuenta, por la confirmación de siempre. */
  async function iniciarBaja(usuario: HceUsuarioListado): Promise<void> {
    if (!sesion) return;
    setError(null);
    if (usuario.role !== 'personal-medico') {
      setConfirmando(usuario.id);
      return;
    }
    setBajaPanel({ id: usuario.id, resumen: null, error: null });
    bajaCargando.current = usuario.id;
    const r = await resumenBajaHceUsuario(sesion.token, usuario.id);
    if (bajaCargando.current !== usuario.id) return; // se canceló mientras cargaba
    bajaCargando.current = null;
    if (!r.ok) {
      setBajaPanel(null);
      setError(r.error);
      return;
    }
    if (bajaNecesitaPanel(r.resumen)) setBajaPanel({ id: usuario.id, resumen: r.resumen, error: null });
    else {
      setBajaPanel(null);
      setConfirmando(usuario.id);
    }
  }

  async function confirmarBaja(usuario: HceUsuarioListado, eleccion = ''): Promise<void> {
    if (!sesion || pendiente === usuario.id) return;
    setConfirmando(null);
    setPendiente(usuario.id);
    setError(null);
    const r = await cambiarEstadoHceUsuario(sesion.token, usuario.id, false, cargaDeBaja(eleccion));
    setPendiente(null);
    if (!r.ok) {
      // Con el cuadro abierto el error sale ahí (la persona sigue eligiendo); sin él, arriba.
      if (bajaPanel?.id === usuario.id) setBajaPanel({ ...bajaPanel, error: r.error });
      else setError(r.error);
      return;
    }
    setBajaPanel(null);
    // Baja de un médico: qué pasó con sus pacientes en espera y qué especialidades quedan sin médico (PLAN Sala, C11.2). Lo que exige
    // acción del admin sale como alerta roja; lo meramente informativo, como aviso.
    const avisoSala = avisoDeBaja(r);
    if (avisoSala?.nivel === 'alerta') setError(avisoSala.texto);
    else if (avisoSala) setAviso(avisoSala.texto);
    await cargar();
  }

  function iniciarEdicionMatricula(usuario: HceUsuarioListado): void {
    setEditandoMatricula(usuario.id);
    setMatriculaEnEdicion(usuario.matricula ?? '');
  }

  function iniciarEdicionConsultorio(usuario: HceUsuarioListado): void {
    setEditandoConsultorio(usuario.id);
    setConsultorioEnEdicion(usuario.consultorio ?? '');
  }

  async function guardarConsultorio(usuarioId: number): Promise<void> {
    if (!sesion) return;
    setGuardandoConsultorio(true);
    setError(null);
    const r = await actualizarConsultorioHceUsuario(sesion.token, usuarioId, consultorioEnEdicion);
    setGuardandoConsultorio(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setEditandoConsultorio(null);
    await cargar();
  }

  async function guardarEspecialidad(usuarioId: number): Promise<void> {
    if (!sesion || !especialidadEnEdicion) return;
    setConfirmandoEspecialidad(false);
    setGuardandoEspecialidad(true);
    setError(null);
    const r = await actualizarEspecialidadHceUsuario(sesion.token, usuarioId, especialidadEnEdicion);
    setGuardandoEspecialidad(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setEditandoEspecialidad(null);
    await cargar();
  }

  async function guardarMatricula(usuarioId: number): Promise<void> {
    if (!sesion) return;
    setGuardandoMatricula(true);
    setError(null);
    const r = await actualizarMatriculaHceUsuario(sesion.token, usuarioId, matriculaEnEdicion);
    setGuardandoMatricula(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setEditandoMatricula(null);
    await cargar();
  }

  /**
   * "Cambiar contraseña" (2026-09-13, pedido de Sue): el admin no elige ni ve
   * contraseñas — se le manda a la persona el enlace para crear una nueva (o
   * se le reenvía la invitación si nunca la usó). El anterior deja de servir.
   */
  async function enviarEnlaceContrasena(usuario: HceUsuarioListado): Promise<void> {
    if (!sesion) return;
    setPendiente(usuario.id);
    setError(null);
    setAviso(null);
    const r = await enviarEnlaceContrasenaHceUsuario(sesion.token, usuario.id);
    setPendiente(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    if (r.correoEnviado) setAviso(t('admin.usuarios.enlaceEnviado', { nombre: usuario.name, correo: usuario.username }));
    else setError(t('admin.usuarios.enlaceNoEnviado', { nombre: usuario.name, detalle: r.detalleCorreo }));
  }

  /** Reactivar (2026-09-13): sin contraseña del admin — la persona recibe el enlace para crear la suya. */
  async function reactivar(usuario: HceUsuarioListado): Promise<void> {
    if (!sesion) return;
    setPendiente(usuario.id);
    setError(null);
    setAviso(null);
    const r = await reactivarHceUsuario(sesion.token, usuario.id);
    setPendiente(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    if (r.correoEnviado) setAviso(t('admin.usuarios.reactivada', { nombre: usuario.name }));
    else setError(t('admin.usuarios.reactivadaSinCorreo', { nombre: usuario.name, detalle: r.detalleCorreo }));
    await cargar();
  }

  /** Reinicia la verificación en dos pasos de otra cuenta (teléfono y códigos de respaldo perdidos): se enrola de nuevo y sus sesiones abiertas se cierran. */
  async function reiniciarMfa(usuario: HceUsuarioListado): Promise<void> {
    if (!sesion) return;
    setConfirmandoMfa(null);
    setPendiente(usuario.id);
    setError(null);
    setAviso(null);
    const r = await reiniciarMfaDeCuenta(sesion.token, usuario.id);
    setPendiente(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setAviso(t('admin.usuarios.mfa.reiniciada', { nombre: usuario.name }));
    await cargar();
  }

  /** Levanta el bloqueo por códigos incorrectos de otra cuenta (queda constancia de quién lo hizo). */
  async function desbloquearMfa(usuario: HceUsuarioListado): Promise<void> {
    if (!sesion) return;
    setPendiente(usuario.id);
    setError(null);
    setAviso(null);
    const r = await desbloquearMfaDeCuenta(sesion.token, usuario.id);
    setPendiente(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setAviso(t('admin.usuarios.mfa.desbloqueada', { nombre: usuario.name }));
    await cargar();
  }

  if (!sesion || sesionVencida) {
    return (
      <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4">
        <Alert variant="red" className="max-w-sm">
          <AlertDescription>{t('admin.usuarios.sesionVencida')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-8">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-medium text-hce-ink">{t('admin.usuarios.titulo')}</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void navigate('/auditoria')}>
              {t('admin.usuarios.irAuditoria')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void navigate('/comite')}>
              {t('comite.titulo')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void navigate('/emergencias')}>
              {t('emergencias.titulo')}
            </Button>
            {/* Bug real encontrado 2026-09-11: esto limpiaba el token pero nunca
                navegaba a ningún lado — la pantalla seguía mostrando la lista de
                usuarios (leída una sola vez al montar) hasta que alguien
                refrescara a mano. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                cerrarSesionHceAdmin();
                void navigate('/login', { replace: true });
              }}
            >
              {t('admin.usuarios.cerrarSesion')}
            </Button>
          </div>
        </div>

        {datos && (
          <div className="rounded-md border border-border bg-hce-surface p-3 text-sm">
            <span className="font-mono uppercase tracking-wide text-xs text-hce-muted mr-2">{t('admin.usuarios.cupo')}</span>
            <span className="font-mono text-hce-ink">
              {datos.personalMedicoActivos} / {datos.cupoPersonalMedico || '—'}
            </span>
          </div>
        )}

        <ExportacionYRespaldo token={sesion.token} />

        <SeguridadMfaPanel token={sesion.token} subdominio={sesion.subdominio} onSesionNueva={() => setVersion((v) => v + 1)} />

        <div className="flex justify-end">
          <Button size="sm" variant={invitando ? 'outline' : 'default'} onClick={() => setInvitando((v) => !v)}>
            {invitando ? t('admin.usuarios.cancelar') : t('admin.usuarios.invitar')}
          </Button>
        </div>
        {invitando && (
          <FormularioInvitacion
            token={sesion.token}
            onInvitado={async (nombre, correo) => {
              setInvitando(false);
              if (correo.enviado) setAviso(t('admin.usuarios.invitadoConCorreo', { nombre }));
              else setError(t('admin.usuarios.invitadoSinCorreo', { nombre, detalle: correo.detalle }));
              await cargar();
            }}
          />
        )}
        {aviso && (
          <Alert>
            <AlertDescription>{aviso}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="red">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {cargando && !datos ? (
          <p className="text-sm text-hce-muted">{t('admin.usuarios.cargando')}</p>
        ) : datos && datos.usuarios.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('admin.usuarios.vacio')}</p>
        ) : datos ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.usuarios.columnas.usuario')}</TableHead>
                <TableHead>{t('admin.usuarios.columnas.rol')}</TableHead>
                <TableHead>{t('admin.usuarios.columnas.especialidad')}</TableHead>
                <TableHead>{t('admin.usuarios.columnas.matricula')}</TableHead>
                <TableHead>{t('admin.usuarios.columnas.consultorio')}</TableHead>
                <TableHead>{t('admin.usuarios.columnas.estado')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {datos.usuarios.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="font-medium text-hce-ink">{u.name}</div>
                    <div className="text-xs font-mono text-hce-muted">{u.username}</div>
                  </TableCell>
                  <TableCell>
                    {u.role === 'admin'
                      ? t('admin.usuarios.rolAdmin')
                      : u.role === 'director-tecnico-farmacia'
                        ? t('admin.usuarios.rolDirectorTecnico')
                        : t('admin.usuarios.rolPersonalMedico')}
                  </TableCell>
                  <TableCell className="text-hce-muted">
                    {u.role === 'personal-medico' && u.active && editandoEspecialidad === u.id ? (
                      <div className="flex items-center gap-2">
                        <Select value={especialidadEnEdicion} onValueChange={(v) => { setEspecialidadEnEdicion(v); setConfirmandoEspecialidad(false); }}>
                          <SelectTrigger aria-label={t('auth.registro.especialidad')}>
                            <SelectValue placeholder={t('auth.registro.especialidadPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {CATALOGO_ESPECIALIDADES_ORDENADO.map((e) => (
                              <SelectItem key={e.codigo} value={e.codigo}>
                                {e.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          disabled={guardandoEspecialidad || !especialidadEnEdicion}
                          onClick={() => (especialidadUsaPoliticaMedico(especialidadEnEdicion) && !confirmandoEspecialidad ? setConfirmandoEspecialidad(true) : void guardarEspecialidad(u.id))}
                        >
                          {guardandoEspecialidad ? t('admin.usuarios.guardando') : confirmandoEspecialidad ? t('admin.usuarios.confirmar') : t('admin.usuarios.guardar')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setEditandoEspecialidad(null); setConfirmandoEspecialidad(false); }}>
                          {t('admin.usuarios.cancelar')}
                        </Button>
                        {confirmandoEspecialidad && (
                          <p role="alert" className="text-xs text-hce-ink">
                            {t('admin.usuarios.confirmarEspecialidadMedico', { nombre: u.name })}
                          </p>
                        )}
                      </div>
                    ) : u.role === 'personal-medico' && u.active ? (
                      <button
                        type="button"
                        className="text-left text-hce-muted hover:text-hce-ink underline decoration-dotted"
                        onClick={() => {
                          setConfirmandoEspecialidad(false);
                          setEditandoEspecialidad(u.id);
                          setEspecialidadEnEdicion(u.especialidadCodigo && especialidadPorCodigo(u.especialidadCodigo) ? u.especialidadCodigo : '');
                        }}
                      >
                        {u.especialidadCodigo ? (especialidadPorCodigo(u.especialidadCodigo)?.nombre ?? t('admin.usuarios.especialidadSinReconocer')) : t('admin.usuarios.sinEspecialidad')}
                      </button>
                    ) : u.especialidadCodigo ? (
                      (especialidadPorCodigo(u.especialidadCodigo)?.nombre ?? u.especialidadCodigo)
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {u.role === 'admin' ? (
                      '—'
                    ) : editandoMatricula === u.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={matriculaEnEdicion}
                          onChange={(e) => setMatriculaEnEdicion(e.target.value)}
                          placeholder={t('admin.usuarios.matriculaPlaceholder')}
                          className="h-7 w-32 text-xs"
                        />
                        <Button size="sm" disabled={guardandoMatricula} onClick={() => void guardarMatricula(u.id)}>
                          {guardandoMatricula ? t('admin.usuarios.guardando') : t('admin.usuarios.guardar')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditandoMatricula(null)}>
                          {t('admin.usuarios.cancelar')}
                        </Button>
                      </div>
                    ) : (
                      <button type="button" className="text-left text-hce-muted hover:text-hce-ink underline decoration-dotted" onClick={() => iniciarEdicionMatricula(u)}>
                        {u.matricula || t('admin.usuarios.sinMatricula')}
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.role !== 'personal-medico' ? (
                      '—'
                    ) : editandoConsultorio === u.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={consultorioEnEdicion}
                          maxLength={40}
                          onChange={(e) => setConsultorioEnEdicion(e.target.value)}
                          placeholder={t('admin.usuarios.consultorioPlaceholder')}
                          aria-label={t('admin.usuarios.columnas.consultorio')}
                          className="h-7 w-32 text-xs"
                        />
                        <Button size="sm" disabled={guardandoConsultorio} onClick={() => void guardarConsultorio(u.id)}>
                          {guardandoConsultorio ? t('admin.usuarios.guardando') : t('admin.usuarios.guardar')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditandoConsultorio(null)}>
                          {t('admin.usuarios.cancelar')}
                        </Button>
                      </div>
                    ) : (
                      <button type="button" className="text-left text-hce-muted hover:text-hce-ink underline decoration-dotted" onClick={() => iniciarEdicionConsultorio(u)}>
                        {u.consultorio || t('admin.usuarios.sinConsultorio')}
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.active ? 'sage' : 'clay'}>
                      {u.active ? t('admin.usuarios.activo') : t('admin.usuarios.inactivo')}
                    </Badge>
                    {u.active && u.mfa && (
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge variant={u.mfa.activo ? 'pine' : 'secondary'}>{u.mfa.activo ? t('admin.usuarios.mfa.activa') : t('admin.usuarios.mfa.sinFactor')}</Badge>
                        {u.mfa.bloqueado && <Badge variant="clay">{t('admin.usuarios.mfa.bloqueada')}</Badge>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {!u.active ? (
                      <Button variant="outline" size="sm" disabled={pendiente === u.id} onClick={() => void reactivar(u)}>
                        {pendiente === u.id ? t('admin.usuarios.enviandoEnlace') : t('admin.usuarios.reactivar')}
                      </Button>
                    ) : bajaPanel?.id === u.id ? (
                      <BajaMedicoPanel
                        nombre={u.name}
                        resumen={bajaPanel.resumen}
                        enviando={pendiente === u.id}
                        error={bajaPanel.error}
                        onConfirmar={(eleccion) => void confirmarBaja(u, eleccion)}
                        onCancelar={() => {
                          bajaCargando.current = null;
                          setBajaPanel(null);
                        }}
                      />
                    ) : confirmando === u.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-hce-clay">{t('admin.usuarios.confirmarBaja', { nombre: u.name })}</span>
                        <Button variant="destructive" size="sm" disabled={pendiente === u.id} onClick={() => void confirmarBaja(u)}>
                          {pendiente === u.id ? t('admin.usuarios.dandoDeBaja') : t('admin.usuarios.confirmar')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmando(null)}>
                          {t('admin.usuarios.cancelar')}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" disabled={pendiente === u.id} onClick={() => void enviarEnlaceContrasena(u)}>
                          {pendiente === u.id ? t('admin.usuarios.enviandoEnlace') : t('admin.usuarios.cambiarContrasena')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => void iniciarBaja(u)}>
                          {t('admin.usuarios.darDeBaja')}
                        </Button>
                        {u.mfa?.bloqueado && !u.propia && (
                          <Button variant="outline" size="sm" disabled={pendiente === u.id} onClick={() => void desbloquearMfa(u)}>
                            {t('admin.usuarios.mfa.desbloquear')}
                          </Button>
                        )}
                        {u.mfa?.activo && !u.propia && (
                          <Button variant="outline" size="sm" disabled={pendiente === u.id} onClick={() => setConfirmandoMfa(u.id)}>
                            {t('admin.usuarios.mfa.reiniciar')}
                          </Button>
                        )}
                      </div>
                    )}
                    {confirmandoMfa === u.id && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-hce-clay">{t('admin.usuarios.mfa.confirmarReinicio', { nombre: u.name })}</span>
                        <Button variant="destructive" size="sm" disabled={pendiente === u.id} onClick={() => void reiniciarMfa(u)}>
                          {t('admin.usuarios.confirmar')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmandoMfa(null)}>
                          {t('admin.usuarios.cancelar')}
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </div>
    </div>
  );
}

type TipoInvitacion = 'personal-medico' | 'director-tecnico-farmacia' | 'admin';

/**
 * Invitar desde el panel — única forma de crear cuentas del HCE desde el
 * 2026-09-13 (cupo, matrícula, especialidad), con el consultorio de la sesión
 * del admin. El admin NO elige contraseña: la bóveda le manda a la persona un
 * correo con su usuario y un enlace para crear la suya.
 */
function FormularioInvitacion({
  token,
  onInvitado,
}: {
  token: string;
  onInvitado: (nombre: string, correo: { enviado: boolean; detalle: string }) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [tipo, setTipo] = useState<TipoInvitacion>('personal-medico');
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [username, setUsername] = useState('');
  const [especialidadCodigo, setEspecialidadCodigo] = useState('');
  const [matricula, setMatricula] = useState('');
  const [cedula, setCedula] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiereMatricula = tipo !== 'admin';
  const incompleto = (tipo === 'personal-medico' && !especialidadCodigo) || (requiereMatricula && !matricula.trim()) || (requiereMatricula && !cedula.trim());

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    const r = await invitarHceUsuario(token, {
      tipo,
      nombre,
      apellido: apellido || undefined,
      username,
      especialidadCodigo: tipo === 'personal-medico' ? especialidadCodigo : undefined,
      matricula: requiereMatricula ? matricula : undefined,
      cedula: requiereMatricula ? cedula : undefined,
    });
    setEnviando(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    await onInvitado([nombre, apellido].filter(Boolean).join(' '), { enviado: r.correoEnviado, detalle: r.detalleCorreo });
  }

  return (
    <form onSubmit={(e) => void enviar(e)} className="space-y-3 rounded-md border border-border bg-hce-surface p-4">
      <p className="text-sm font-medium text-hce-ink">{t('admin.usuarios.invitarTitulo')}</p>
      <p className="text-xs text-hce-muted">{t('admin.usuarios.invitarAviso')}</p>
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Select value={tipo} onValueChange={(v) => setTipo(v as TipoInvitacion)}>
        <SelectTrigger aria-label={t('auth.registro.tipoCuenta')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="personal-medico">{t('auth.registro.tipoPersonalMedico')}</SelectItem>
          <SelectItem value="director-tecnico-farmacia">{t('auth.registro.tipoDirectorTecnicoFarmacia')}</SelectItem>
          <SelectItem value="admin">{t('auth.registro.tipoAdmin')}</SelectItem>
        </SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-2">
        <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={t('auth.registro.nombre')} aria-label={t('auth.registro.nombre')} required />
        <Input value={apellido} onChange={(e) => setApellido(e.target.value)} placeholder={t('auth.registro.apellido')} aria-label={t('auth.registro.apellido')} />
      </div>
      <Input
        type="email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder={t('admin.usuarios.correo')}
        aria-label={t('admin.usuarios.correo')}
        autoCapitalize="none"
        autoCorrect="off"
        required
      />
      {tipo === 'personal-medico' && (
        <Select value={especialidadCodigo} onValueChange={setEspecialidadCodigo}>
          <SelectTrigger aria-label={t('auth.registro.especialidad')}>
            <SelectValue placeholder={t('auth.registro.especialidadPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {CATALOGO_ESPECIALIDADES_ORDENADO.map((esp) => (
              <SelectItem key={esp.codigo} value={esp.codigo}>
                {esp.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {requiereMatricula && (
        <Input
          value={matricula}
          onChange={(e) => setMatricula(e.target.value)}
          placeholder={tipo === 'director-tecnico-farmacia' ? t('auth.registro.matriculaFarmaciaPlaceholder') : t('auth.registro.matriculaPlaceholder')}
          aria-label={t('auth.registro.matricula')}
          required
        />
      )}
      {requiereMatricula && (
        <Input
          value={cedula}
          onChange={(e) => setCedula(e.target.value)}
          placeholder={t('auth.registro.cedulaPlaceholder')}
          aria-label={t('auth.registro.cedula')}
          required
        />
      )}
      <Button type="submit" size="sm" disabled={enviando || incompleto}>
        {enviando ? t('auth.registro.creando') : t('auth.registro.crear')}
      </Button>
    </form>
  );
}
